import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fetchLMSReconReport, LMSReconColumn } from '../services/lmsReconService';
import { exportQueryResponse } from '../services/exportService';
import { Widget } from '../types/widget';
import { WidgetCard } from './WidgetCard';

export type LMSReconReportKey =
  | 'progress-report'
  | 'rental-recon-summary'
  | 'utilities-vs-rent'
  | 'invoiced-vs-paid-rent'
  | 'invoiced-vs-paid-utilities'
  | 'contractual-charges'
  | 'variable-charges'
  | 'escalations-rent'
  | 'escalations-other'
  | 'growth-rent'
  | 'growth-utilities'
  | 'total-rate-per-sqm'
  | 'rental-monthend-batch-report'
  | 'rental-arrears-batch-report'
  | 'municipal-monthend-batch-report'
  | 'municipal-arrears-batch-report'
  | 'transaction-report';

type ProgressMode = 'current-rental' | 'current-municipal' | 'previous-rental' | 'previous-municipal';
type BatchFilterKey = 'year' | 'month' | 'batch_no';
type BatchFilters = Record<BatchFilterKey, string>;
type TransactionFilterKey = 'branch_code' | 'year' | 'month';
type TransactionFilters = Record<TransactionFilterKey, string>;
type UtilityFilterKey = 'date' | 'province' | 'district';
type UtilityFilters = Record<UtilityFilterKey, string>;
type LocationFilterKey = 'date' | 'province' | 'district';
type LocationFilters = Record<LocationFilterKey, string>;
type SortDirection = 'asc' | 'desc';
interface TableSort {
  key: string;
  direction: SortDirection;
}

export interface ReportVisualContext {
  source_type: 'static_widget';
  source_id: string;
  title: string;
  users_query: string;
  visualization_type: string;
  row_count: number;
  visible_columns: string[];
  numeric_columns: string[];
  data_sample: Record<string, unknown>[];
  sample_limit: number;
}

export interface LMSReconContext {
  report_family: 'lms-recon';
  active_tab: LMSReconReportKey;
  report_title: string;
  report_view: string;
  progress_period?: 'current' | 'previous';
  progress_domain?: 'rental' | 'municipal';
  batch_filters?: Partial<BatchFilters>;
  transaction_filters?: Partial<TransactionFilters>;
  utility_filters?: Partial<UtilityFilters>;
  location_filters?: Partial<LocationFilters>;
  visual_contexts?: ReportVisualContext[];
}

interface LMSReconDashboardProps {
  activeTab: LMSReconReportKey;
  onAskMore?: (contextQuery: string, contextData?: any) => void;
  onContextChange?: (context: LMSReconContext) => void;
}

interface Metric {
  label: string;
  value: string;
}

const VISUAL_CONTEXT_SAMPLE_LIMIT = 10;

const VisualLoadingCard: React.FC<{ title?: string }> = ({ title }) => (
  <div className="flex h-full min-h-[120px] flex-col rounded-lg border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/80">
    <div className="mb-4 h-4 w-44 animate-pulse rounded bg-slate-200 dark:bg-slate-700">
      <span className="sr-only">{title ? `Loading ${title}` : 'Loading visual'}</span>
    </div>
    <div className="flex min-h-24 flex-1 items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-slate-500 dark:text-slate-400">
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600 dark:border-blue-900 dark:border-t-blue-300" />
        <span className="text-xs font-semibold uppercase tracking-wide">Loading data</span>
      </div>
    </div>
  </div>
);

const TableLoadingRows: React.FC<{ columnCount: number; rowCount?: number }> = ({ columnCount, rowCount = 6 }) => {
  const safeColumnCount = Math.max(columnCount, 1);
  return (
    <>
      {Array.from({ length: rowCount }).map((_, rowIndex) => (
        <tr key={`loading-row-${rowIndex}`} className="animate-pulse bg-white dark:bg-slate-900">
          {Array.from({ length: safeColumnCount }).map((__, columnIndex) => (
            <td key={`loading-cell-${rowIndex}-${columnIndex}`} className="px-4 py-3">
              <div className="h-4 w-full min-w-24 rounded bg-slate-200 dark:bg-slate-700" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
};

export const lmsReconReportTabs: Array<[LMSReconReportKey, string]> = [
  ['progress-report', 'Progress Report'],
  ['rental-recon-summary', 'Rental Recon Summary'],
  ['utilities-vs-rent', 'Utilities vs. Rent'],
  ['invoiced-vs-paid-rent', 'Invoiced vs. Paid - Rent'],
  ['invoiced-vs-paid-utilities', 'Invoiced vs. Paid - Utilities'],
  ['contractual-charges', 'Contractual Charges'],
  ['variable-charges', 'Variable Charges'],
  ['escalations-rent', 'Escalations - Rent'],
  ['escalations-other', 'Escalations - Other'],
  ['growth-rent', 'Growth - Rent'],
  ['growth-utilities', 'Growth - Utilities'],
  ['total-rate-per-sqm', 'Total Rate per SQM'],
  ['rental-monthend-batch-report', 'Rental Monthend Batch Report'],
  ['rental-arrears-batch-report', 'Rental Arrears Batch Report'],
  ['municipal-monthend-batch-report', 'Municipal Monthend Batch Report'],
  ['municipal-arrears-batch-report', 'Municipal Arrears Batch Report'],
  ['transaction-report', 'Transaction Report'],
];

const progressModes: Array<[ProgressMode, string]> = [
  ['current-rental', 'Current - Rental'],
  ['current-municipal', 'Current - Municipal'],
  ['previous-rental', 'Last Month - Rental'],
  ['previous-municipal', 'Last Month - Municipal'],
];

const batchReportKeys = new Set<LMSReconReportKey>([
  'rental-monthend-batch-report',
  'rental-arrears-batch-report',
  'municipal-monthend-batch-report',
  'municipal-arrears-batch-report',
]);

const growthReportKeys = new Set<LMSReconReportKey>(['growth-rent', 'growth-utilities']);

const batchFilterDefinitions: Array<{ key: BatchFilterKey; label: string }> = [
  { key: 'year', label: 'Year' },
  { key: 'month', label: 'Month' },
  { key: 'batch_no', label: 'BatchNo' },
];

const batchReportDefaultFilters: Partial<Record<LMSReconReportKey, BatchFilters>> = {
  'rental-monthend-batch-report': { year: '2024', month: '10', batch_no: '1' },
  'municipal-monthend-batch-report': { year: '2026', month: '2', batch_no: '1' },
};

const transactionFilterDefinitions: Array<{ key: TransactionFilterKey; label: string }> = [
  { key: 'branch_code', label: 'branch_code' },
  { key: 'year', label: 'Year' },
  { key: 'month', label: 'Month' },
];

const utilityFilterDefinitions: Array<{ key: UtilityFilterKey; label: string }> = [
  { key: 'date', label: 'Date' },
  { key: 'province', label: 'Province' },
  { key: 'district', label: 'District' },
];

const locationFilterDefinitions: Array<{ key: LocationFilterKey; label: string }> = [
  { key: 'date', label: 'Date' },
  { key: 'province', label: 'Province' },
  { key: 'district', label: 'District' },
];

const invoicedVsPaidColumnOrder = [
  'branch code',
  'branch name',
  'date',
  'category',
  'sub-category',
  'invoiced',
  'paid',
  'escalation / contract amount',
  'prior month paid',
  'paid + appa + mpob',
  'recommended paid',
  'store benchmark',
  'store trendline',
  'global benchmark',
];

const contractualChargesColumnOrder = [
  'branch code',
  'branch name',
  'date',
  'category',
  'sub-category',
  'invoiced',
  'paid',
  'paid + appa + mpob',
  'escalation / contract amount',
  'prior month paid',
];

const escalationRentColumnOrder = [
  'branch code',
  'branch name',
  'date',
  'category',
  'sub-category',
  'lease commencement date',
  'lease expiry date',
  'escalation frequency (every n months)',
  'escalation %',
  'compound escalation %',
  'contract amount',
  'invoiced',
  'paid',
  'invoiced % of contract amount',
  'paid % of contract amount',
  'paid % of invoiced amount',
];

const growthRentColumnOrder = [
  'branch code',
  'branch name',
  'province',
  'district',
  'date',
  'category',
  'sub-category',
  'invoiced',
  'paid',
  'last 1 month invoiced',
  'last 1 month invoiced (growth %)',
  'last 1 month paid',
  'last 1 month paid (growth %)',
  'last 3 month invoiced',
  'last 3 month invoiced (growth %)',
  'last 3 month paid',
  'last 3 month paid (growth %)',
  'last 6 month invoiced',
  'last 6 month invoiced (growth %)',
  'last 6 month paid',
  'last 6 month paid (growth %)',
  'last 12 month invoiced',
  'last 12 month invoiced (growth %)',
  'last 12 month paid',
  'last 12 month paid (growth %)',
];

const growthUtilitiesColumnOrder = [
  'branch code',
  'branch name',
  'province',
  'district',
  'date',
  'invoiced',
  'paid',
  'last 1 month invoiced',
  'last 1 month invoiced (growth %)',
  'last 1 month paid',
  'last 1 month paid (growth %)',
  'last 3 month invoiced',
  'last 3 month invoiced (growth %)',
  'last 3 month paid',
  'last 3 month paid (growth %)',
  'last 6 month invoiced',
  'last 6 month invoiced (growth %)',
  'last 6 month paid',
  'last 6 month paid (growth %)',
  'last 12 month invoiced',
  'last 12 month invoiced (growth %)',
  'last 12 month paid',
  'last 12 month paid (growth %)',
];

const transactionCategoryOrder = ['Invoiced', 'Paid', 'Previous statement balance'];
const transactionSubCategoryOrder = [
  'Electricity',
  'Interest',
  'Lease Fees',
  'Rates and Taxes',
  'Rates & Taxes',
  'Refuse',
  'Rent',
  'Sewerage',
  'Water',
];

const progressStatuses = [
  'In Progress',
  'Awaiting Approval',
  'Awaiting Exception Approval',
  'Approved',
];

const rentalSummaryDifferenceColumnKeys = [
  'branch_code',
  'branch_name',
  'date',
  'landlord_statement_balance',
  'statement_balance',
  'statement_difference',
  'invoice_captured_by',
];

const statementDiffNumericColumnKeys = new Set([
  'landlord_statement_balance',
  'statement_balance',
  'statement_difference',
]);

const getReportTitle = (activeTab: LMSReconReportKey): string =>
  lmsReconReportTabs.find(([key]) => key === activeTab)?.[1] || 'LMS Recon Analysis';

const asNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const formatMetricValue = (value: unknown): string => {
  const numeric = asNumber(value);
  if (numeric === null) {
    return value === null || value === undefined || value === '' ? 'N/A' : String(value);
  }
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: Math.abs(numeric) >= 100 ? 0 : 2,
  }).format(numeric);
};

const formatBatchAmount = (value: unknown): string => {
  const numeric = asNumber(value);
  if (numeric === null) {
    return '';
  }
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
};

const formatFixedNumber = (value: unknown, fractionDigits = 2): string => {
  const numeric = asNumber(value);
  if (numeric === null) {
    return '';
  }
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(numeric);
};

const formatCurrencyValue = (value: unknown): string => {
  const numeric = asNumber(value);
  if (numeric === null) return '';
  const formatted = formatFixedNumber(Math.abs(numeric));
  if (!formatted) return '';
  return numeric < 0 ? `-R${formatted}` : `R${formatted}`;
};

const formatPercentValue = (value: unknown): string => {
  const formattedValue = formatFixedNumber(value);
  return formattedValue ? `${formattedValue}%` : '';
};

const utilityCurrencyColumnKeys = new Set([
  'invoiced_rent',
  'invoiced_utilities',
  'paid_rent',
  'paid_utilities',
]);

const utilityPercentColumnKeys = new Set([
  'invoiced_utility_rent_percent',
  'paid_utility_rent_percent',
]);

const ratePerSqmAverageColumnKeys = new Set([
  'invoiced_sqm_rent',
  'invoiced_sqm_utilities',
  'paid_sqm_rent',
  'paid_sqm_utilities',
]);

const growthPercentColumnKeys = new Set([
  'last_1_month_invoiced_growth',
  'last_1_month_paid_growth',
  'last_3_month_invoiced_growth',
  'last_3_month_paid_growth',
  'last_6_month_invoiced_growth',
  'last_6_month_paid_growth',
  'last_12_month_invoiced_growth',
  'last_12_month_paid_growth',
]);

const escalationPercentColumnKeys = new Set([
  'invoiced_contract_percent',
  'paid_contract_percent',
  'paid_invoiced_percent',
]);


const formatBatchCellValue = (column: LMSReconColumn, value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  if (column.currency) {
    return formatCurrencyValue(value);
  }
  return String(value);
};

const valueText = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return String(value);
};

const normalise = (value: unknown): string => valueText(value).trim().toLowerCase();

const compareTableValues = (first: unknown, second: unknown): number => {
  const firstNumber = asNumber(first);
  const secondNumber = asNumber(second);
  if (firstNumber !== null && secondNumber !== null) {
    return firstNumber - secondNumber;
  }

  const firstTime = Date.parse(valueText(first));
  const secondTime = Date.parse(valueText(second));
  if (Number.isFinite(firstTime) && Number.isFinite(secondTime)) {
    return firstTime - secondTime;
  }

  return valueText(first).localeCompare(valueText(second), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

const sortRowsByColumn = (
  sourceRows: Record<string, unknown>[],
  sort: TableSort | null,
): Record<string, unknown>[] => {
  if (!sort) {
    return sourceRows;
  }

  const directionMultiplier = sort.direction === 'asc' ? 1 : -1;
  return [...sourceRows].sort((first, second) => (
    compareTableValues(first[sort.key], second[sort.key]) * directionMultiplier
  ));
};

const createEmptyBatchFilters = (): BatchFilters => ({
  year: '',
  month: '',
  batch_no: '',
});

const createDefaultBatchFilters = (reportKey: LMSReconReportKey): BatchFilters => ({
  ...createEmptyBatchFilters(),
  ...(batchReportDefaultFilters[reportKey] || {}),
});

const createEmptyBatchFilterOptions = (): Record<BatchFilterKey, string[]> => ({
  year: [],
  month: [],
  batch_no: [],
});

const createEmptyTransactionFilters = (): TransactionFilters => {
  const now = new Date();
  return {
    branch_code: '',
    year: String(now.getFullYear()),
    month: String(now.getMonth() + 1),
  };
};

const createEmptyTransactionFilterOptions = (): Record<TransactionFilterKey, string[]> => ({
  branch_code: [],
  year: [],
  month: [],
});

const createEmptyUtilityFilters = (): UtilityFilters => ({
  date: '',
  province: '',
  district: '',
});

const createEmptyUtilityFilterOptions = (): Record<UtilityFilterKey, string[]> => ({
  date: [],
  province: [],
  district: [],
});

const createEmptyLocationFilters = (): LocationFilters => ({
  date: '',
  province: '',
  district: '',
});

const createEmptyLocationFilterOptions = (): Record<LocationFilterKey, string[]> => ({
  date: [],
  province: [],
  district: [],
});

const sortOptionValues = (first: string, second: string): number => {
  const firstNumber = Number(first);
  const secondNumber = Number(second);
  if (Number.isFinite(firstNumber) && Number.isFinite(secondNumber)) {
    return firstNumber - secondNumber;
  }
  return first.localeCompare(second, undefined, { numeric: true, sensitivity: 'base' });
};

const buildFilterOptions = (rows: Record<string, unknown>[], key: string): string[] => {
  const options = new Set<string>();
  rows.forEach((row) => {
    const value = valueText(row[key]).trim();
    if (value) {
      options.add(value);
    }
  });
  return Array.from(options).sort(sortOptionValues);
};

const filterOptionsFromSummary = (
  summary: Record<string, any> | undefined,
  key: string,
  fallbackRows: Record<string, unknown>[],
): string[] => {
  const sourceOptions = summary?.filter_options?.[key];
  if (Array.isArray(sourceOptions) && sourceOptions.length) {
    return sourceOptions
      .map((option: string | number) => String(option))
      .filter(Boolean)
      .sort(sortOptionValues);
  }
  return buildFilterOptions(fallbackRows, key);
};

interface TransactionMatrixColumn {
  key: string;
  year: string;
  month?: string;
  label: string;
  type: 'month' | 'year-total' | 'grand-total';
}

interface TransactionMatrixYearGroup {
  year: string;
  columns: TransactionMatrixColumn[];
}

interface TransactionMatrixRow {
  key: string;
  label: string;
  level: 0 | 1;
  values: Record<string, number>;
  bold?: boolean;
}

interface TransactionMatrix {
  yearGroups: TransactionMatrixYearGroup[];
  columns: TransactionMatrixColumn[];
  rows: TransactionMatrixRow[];
}

const formatMatrixValue = (value: number | null): string => {
  if (value === null || Math.abs(value) < 0.005) {
    return '';
  }
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const addMatrixValue = (values: Record<string, number>, key: string, amount: number) => {
  values[key] = (values[key] || 0) + amount;
};

const matrixSort = (order: string[]) => (first: string, second: string): number => {
  const firstIndex = order.findIndex((item) => item.toLowerCase() === first.toLowerCase());
  const secondIndex = order.findIndex((item) => item.toLowerCase() === second.toLowerCase());
  if (firstIndex >= 0 || secondIndex >= 0) {
    return (firstIndex >= 0 ? firstIndex : Number.MAX_SAFE_INTEGER) -
      (secondIndex >= 0 ? secondIndex : Number.MAX_SAFE_INTEGER);
  }
  return first.localeCompare(second, undefined, { numeric: true, sensitivity: 'base' });
};

const getMatrixCellValue = (row: TransactionMatrixRow, column: TransactionMatrixColumn): number | null => {
  if (column.type === 'month') {
    return row.values[column.key] ?? null;
  }
  if (column.type === 'year-total') {
    const total = Object.entries(row.values).reduce((sum, [key, value]) => {
      return key.startsWith(`${column.year}-`) ? sum + value : sum;
    }, 0);
    return total || null;
  }
  const total = Object.values(row.values).reduce((sum, value) => sum + value, 0);
  return total || null;
};

const buildTransactionMatrix = (rows: Record<string, unknown>[]): TransactionMatrix => {
  const periodMap = new Map<string, { year: string; month: string }>();
  const categoryMap = new Map<
    string,
    {
      values: Record<string, number>;
      subcategories: Map<string, Record<string, number>>;
    }
  >();
  const totalValues: Record<string, number> = {};

  rows.forEach((row) => {
    const year = valueText(row.year).trim();
    const month = valueText(row.month).trim();
    const category = valueText(row.category).trim() || 'Unassigned';
    const subCategory = valueText(row.sub_category).trim();
    const numericValue = asNumber(row.value);

    if (!year || !month || numericValue === null) {
      return;
    }

    const periodKey = `${year}-${month.padStart(2, '0')}`;
    periodMap.set(periodKey, { year, month });

    if (!categoryMap.has(category)) {
      categoryMap.set(category, { values: {}, subcategories: new Map() });
    }

    const categoryEntry = categoryMap.get(category);
    if (!categoryEntry) {
      return;
    }

    addMatrixValue(categoryEntry.values, periodKey, numericValue);
    addMatrixValue(totalValues, periodKey, numericValue);

    if (subCategory && subCategory.toLowerCase() !== category.toLowerCase()) {
      if (!categoryEntry.subcategories.has(subCategory)) {
        categoryEntry.subcategories.set(subCategory, {});
      }
      const subCategoryValues = categoryEntry.subcategories.get(subCategory);
      if (subCategoryValues) {
        addMatrixValue(subCategoryValues, periodKey, numericValue);
      }
    }
  });

  const periods = Array.from(periodMap.values()).sort((first, second) => {
    const yearComparison = sortOptionValues(first.year, second.year);
    return yearComparison || sortOptionValues(first.month, second.month);
  });

  const groupedPeriods = periods.reduce<Map<string, string[]>>((groups, period) => {
    if (!groups.has(period.year)) {
      groups.set(period.year, []);
    }
    groups.get(period.year)?.push(period.month);
    return groups;
  }, new Map());

  const yearGroups = Array.from(groupedPeriods.entries()).map(([year, months]) => {
    const monthColumns = months.map<TransactionMatrixColumn>((month) => ({
      key: `${year}-${month.padStart(2, '0')}`,
      year,
      month,
      label: month,
      type: 'month',
    }));
    return {
      year,
      columns: [
        ...monthColumns,
        {
          key: `${year}-total`,
          year,
          label: 'Total',
          type: 'year-total' as const,
        },
      ],
    };
  });

  const columns = [
    ...yearGroups.flatMap((group) => group.columns),
    {
      key: 'grand-total',
      year: 'Total',
      label: 'Total',
      type: 'grand-total' as const,
    },
  ];

  const matrixRows: TransactionMatrixRow[] = [];
  const sortedCategories = Array.from(categoryMap.keys()).sort(matrixSort(transactionCategoryOrder));
  sortedCategories.forEach((category) => {
    const categoryEntry = categoryMap.get(category);
    if (!categoryEntry) {
      return;
    }
    matrixRows.push({
      key: `category-${category}`,
      label: category,
      level: 0,
      values: categoryEntry.values,
      bold: true,
    });

    Array.from(categoryEntry.subcategories.keys())
      .sort(matrixSort(transactionSubCategoryOrder))
      .forEach((subCategory) => {
        matrixRows.push({
          key: `subcategory-${category}-${subCategory}`,
          label: subCategory,
          level: 1,
          values: categoryEntry.subcategories.get(subCategory) || {},
        });
      });
  });

  if (matrixRows.length > 0) {
    matrixRows.push({
      key: 'total',
      label: 'Total',
      level: 0,
      values: totalValues,
      bold: true,
    });
  }

  return { yearGroups, columns, rows: matrixRows };
};

const progressModeDetails = (mode: ProgressMode) => {
  const [period, domain] = mode.split('-') as ['current' | 'previous', 'rental' | 'municipal'];
  return {
    period,
    domain,
    statusKey: domain === 'rental' ? 'rental_status' : 'municipal_status',
  };
};

const buildMetrics = (
  rows: Record<string, unknown>[],
  columns: LMSReconColumn[],
  summary?: Record<string, any>,
): Metric[] => {
  const metrics: Metric[] = [{ label: 'Rows', value: formatMetricValue(rows.length) }];
  const totals = summary?.numeric_totals || {};
  const numericColumns = columns.filter((column) => column.numeric && totals[column.key] !== undefined).slice(0, 3);

  numericColumns.forEach((column) => {
    metrics.push({
      label: column.label,
      value: formatMetricValue(totals[column.key]),
    });
  });

  return metrics;
};

const makeReconWidget = (
  id: string,
  title: string,
  usersQuery: string,
  visualizationType: string,
  data: any[],
): Widget => {
  const createdAt = new Date().toISOString();
  return {
    id,
    user_id: 'report',
    title,
    users_query: usersQuery,
    generated_sql_query: 'Built-in LMS Recon report dataset',
    query_response: JSON.stringify(data),
    data,
    last_data_snapshot: data,
    context_history: [],
    visualization_type: visualizationType,
    created_at: createdAt,
    updated_at: createdAt,
  };
};

const metricWidget = (id: string, title: string, query: string, value: string): Widget =>
  makeReconWidget(id, title, query, 'metric_card', [{ value }]);

const parseWidgetRows = (widget: Widget): Record<string, unknown>[] => {
  if (Array.isArray(widget.data)) {
    return widget.data.filter((row): row is Record<string, unknown> => row && typeof row === 'object' && !Array.isArray(row));
  }
  if (Array.isArray(widget.last_data_snapshot)) {
    return widget.last_data_snapshot.filter((row): row is Record<string, unknown> => row && typeof row === 'object' && !Array.isArray(row));
  }
  try {
    const parsed = JSON.parse(widget.query_response);
    const rows = Array.isArray(parsed) ? parsed : [parsed];
    return rows.filter((row): row is Record<string, unknown> => row && typeof row === 'object' && !Array.isArray(row));
  } catch {
    return [];
  }
};

const buildWidgetVisualContext = (widget: Widget): ReportVisualContext => {
  const rows = parseWidgetRows(widget);
  const dataSample = rows.slice(0, VISUAL_CONTEXT_SAMPLE_LIMIT);
  const firstRow = dataSample[0];
  const visibleColumns = firstRow ? Object.keys(firstRow) : [];
  const numericColumns = visibleColumns.filter((column) =>
    dataSample.some((row) => asNumber(row[column]) !== null)
  );

  return {
    source_type: 'static_widget',
    source_id: widget.id,
    title: widget.title,
    users_query: widget.users_query,
    visualization_type: widget.visualization_type,
    row_count: rows.length,
    visible_columns: visibleColumns,
    numeric_columns: numericColumns,
    data_sample: dataSample,
    sample_limit: VISUAL_CONTEXT_SAMPLE_LIMIT,
  };
};

const capturedByChartRows = (rows: Record<string, unknown>[]): Array<{ Employee: string; 'Invoices Captured': number }> => {
  const grouped = new Map<string, number>();
  rows.forEach((row) => {
    const employee = valueText(row.captured_by).trim() || '(Blank)';
    grouped.set(employee, (grouped.get(employee) || 0) + 1);
  });

  return Array.from(grouped.entries())
    .map(([employee, count]) => ({
      Employee: employee,
      'Invoices Captured': count,
    }))
    .sort((first, second) => second['Invoices Captured'] - first['Invoices Captured']);
};

const averageByDimension = (
  rows: Record<string, unknown>[],
  dimensionKey: string,
  valueKey: string,
  dimensionLabel: string,
  valueLabel: string,
): Record<string, string | number>[] => {
  const grouped = new Map<string, { total: number; count: number }>();
  rows.forEach((row) => {
    const dimension = valueText(row[dimensionKey]).trim() || '(Blank)';
    const value = asNumber(row[valueKey]);
    if (value === null) {
      return;
    }
    const entry = grouped.get(dimension) || { total: 0, count: 0 };
    entry.total += value;
    entry.count += 1;
    grouped.set(dimension, entry);
  });

  return Array.from(grouped.entries())
    .map(([dimension, entry]) => ({
      [dimensionLabel]: dimension,
      [valueLabel]: entry.count ? Number((entry.total / entry.count).toFixed(2)) : 0,
    }))
    .sort((first, second) => Number(second[valueLabel]) - Number(first[valueLabel]));
};

const sumByDimension = (
  rows: Record<string, unknown>[],
  dimensionKey: string,
  valueKey: string,
  dimensionLabel: string,
  valueLabel: string,
  limit?: number,
): Record<string, string | number>[] => {
  const grouped = new Map<string, number>();
  rows.forEach((row) => {
    const dimension = valueText(row[dimensionKey]).trim() || '(Blank)';
    grouped.set(dimension, (grouped.get(dimension) || 0) + (asNumber(row[valueKey]) || 0));
  });

  return Array.from(grouped.entries())
    .map(([dimension, value]) => ({
      [dimensionLabel]: dimension,
      [valueLabel]: Number(value.toFixed(2)),
    }))
    .sort((first, second) => Number(second[valueLabel]) - Number(first[valueLabel]))
    .slice(0, limit);
};

const buildGrowthComparisonRows = (rows: Record<string, unknown>[]): Record<string, string | number>[] => {
  const grouped = new Map<
    string,
    {
      invoiced: number;
      last1: number;
      last3: number;
      last6: number;
      last12: number;
    }
  >();

  rows.forEach((row) => {
    const branchCode = valueText(row.branch_code).trim();
    if (!branchCode) {
      return;
    }
    const entry = grouped.get(branchCode) || {
      invoiced: 0,
      last1: 0,
      last3: 0,
      last6: 0,
      last12: 0,
    };
    entry.invoiced += asNumber(row.invoiced) || 0;
    entry.last1 += asNumber(row.last_1_month_invoiced) || 0;
    entry.last3 += asNumber(row.last_3_month_invoiced) || 0;
    entry.last6 += asNumber(row.last_6_month_invoiced) || 0;
    entry.last12 += asNumber(row.last_12_month_invoiced) || 0;
    grouped.set(branchCode, entry);
  });

  return Array.from(grouped.entries())
    .map(([branchCode, entry]) => ({
      'Branch Code': branchCode,
      Invoiced: Number(entry.invoiced.toFixed(2)),
      'Last 1 Month Invoiced': Number(entry.last1.toFixed(2)),
      'Last 3 Month Invoiced': Number(entry.last3.toFixed(2)),
      'Last 6 Month Invoiced': Number(entry.last6.toFixed(2)),
      'Last 12 Month Invoiced': Number(entry.last12.toFixed(2)),
    }))
    .sort((first, second) => Number(second.Invoiced) - Number(first.Invoiced));
};

const normaliseGrowthCoordinates = (
  rawLatitude: number | null,
  rawLongitude: number | null,
): { latitude: number; longitude: number } | null => {
  if (rawLatitude === null || rawLongitude === null) {
    return null;
  }

  let latitude = rawLatitude;
  let longitude = rawLongitude;

  if (Math.abs(latitude) > 90 && Math.abs(longitude) <= 90) {
    latitude = rawLongitude;
    longitude = rawLatitude;
  } else if (latitude >= 15 && latitude <= 35 && longitude >= -35 && longitude <= -15) {
    latitude = rawLongitude;
    longitude = rawLatitude;
  }

  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
    return null;
  }

  const isNullIsland = Math.abs(latitude) < 0.0001 && Math.abs(longitude) < 0.0001;
  const isSouthernAfricaArea = latitude >= -45 && latitude <= 5 && longitude >= -40 && longitude <= 60;
  if (isNullIsland || !isSouthernAfricaArea) {
    return null;
  }

  return { latitude, longitude };
};

const buildGrowthLocationRows = (rows: Record<string, unknown>[]): Record<string, string | number>[] => {
  const grouped = new Map<string, {
    latitude: number;
    longitude: number;
    growthTotal: number;
    growthCount: number;
    branchCode: string;
  }>();

  rows.forEach((row) => {
    const coordinates = normaliseGrowthCoordinates(asNumber(row.latitude), asNumber(row.longitude));
    const growth = asNumber(row.last_1_month_invoiced_growth);
    if (!coordinates || growth === null) {
      return;
    }

    const { latitude, longitude } = coordinates;
    const locationKey = `${latitude.toFixed(6)}|${longitude.toFixed(6)}`;
    const branchCode = valueText(row.branch_code).trim();
    const current = grouped.get(locationKey) || {
      latitude,
      longitude,
      growthTotal: 0,
      growthCount: 0,
      branchCode,
    };
    current.growthTotal += growth;
    current.growthCount += 1;
    if (!current.branchCode || (branchCode && branchCode.localeCompare(current.branchCode) < 0)) {
      current.branchCode = branchCode;
    }
    grouped.set(locationKey, current);
  });

  return Array.from(grouped.values())
    .map((location) => {
      const averageGrowth = location.growthTotal / location.growthCount;
      return {
        'Branch Code': location.branchCode,
        Longitude: location.longitude,
        Latitude: location.latitude,
        'Growth (1 Month)': Number(averageGrowth.toFixed(2)),
      };
    })
    .filter((location) => Number(location['Growth (1 Month)']) > 0)
    .sort((first, second) => Number(second['Growth (1 Month)']) - Number(first['Growth (1 Month)']))
    .slice(0, 3502);
};

const buildUtilityScatterRows = (rows: Record<string, unknown>[]): Record<string, string | number>[] => {
  const grouped = new Map<
    string,
    {
      rent: number;
      utilities: number;
      percentTotal: number;
      percentCount: number;
    }
  >();

  rows.forEach((row) => {
    const branchCode = valueText(row.branch_code).trim();
    if (!branchCode) {
      return;
    }

    const entry = grouped.get(branchCode) || {
      rent: 0,
      utilities: 0,
      percentTotal: 0,
      percentCount: 0,
    };
    entry.rent += asNumber(row.invoiced_rent) || 0;
    entry.utilities += asNumber(row.invoiced_utilities) || 0;

    const percent = asNumber(row.invoiced_utility_rent_percent);
    if (percent !== null) {
      entry.percentTotal += percent;
      entry.percentCount += 1;
    }

    grouped.set(branchCode, entry);
  });

  return Array.from(grouped.entries())
    .map(([branchCode, entry]) => ({
      'Branch Code': branchCode,
      'Sum of Invoiced (Rent)': Number(entry.rent.toFixed(2)),
      'Sum of Invoiced (Utilities)': Number(entry.utilities.toFixed(2)),
      'Average of Invoiced (Utility/Rent) %': entry.percentCount
        ? Number((entry.percentTotal / entry.percentCount).toFixed(2))
        : 0,
    }))
    .sort((first, second) => Number(second['Sum of Invoiced (Rent)']) - Number(first['Sum of Invoiced (Rent)']));
};

const buildRatePerSqmScatterRows = (rows: Record<string, unknown>[]): Record<string, string | number>[] =>
  rows
    .map((row) => ({
      'Branch Code': valueText(row.branch_code).trim(),
      'Lease SQM': asNumber(row.lease_sqm) || 0,
      'Invoiced/SQM (Rent)': asNumber(row.invoiced_sqm_rent) || 0,
    }))
    .filter((row) => row['Branch Code'] && row['Lease SQM'] > 0)
    .sort((first, second) => Number(second['Lease SQM']) - Number(first['Lease SQM']))
    .slice(0, 500);

const LMSReconDashboard: React.FC<LMSReconDashboardProps> = ({ activeTab, onAskMore, onContextChange }) => {
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [columns, setColumns] = useState<LMSReconColumn[]>([]);
  const [summary, setSummary] = useState<Record<string, any> | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [batchFilters, setBatchFilters] = useState<BatchFilters>(() => createDefaultBatchFilters(activeTab));
  const [transactionFilters, setTransactionFilters] = useState<TransactionFilters>(() => createEmptyTransactionFilters());
  const [utilityFilters, setUtilityFilters] = useState<UtilityFilters>(() => createEmptyUtilityFilters());
  const [locationFilters, setLocationFilters] = useState<LocationFilters>(() => createEmptyLocationFilters());
  const [fetchReadyTab, setFetchReadyTab] = useState<LMSReconReportKey>(activeTab);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [progressMode, setProgressMode] = useState<ProgressMode>('current-rental');
  const [exportingTable, setExportingTable] = useState<string | null>(null);
  const [tableSort, setTableSort] = useState<TableSort | null>(null);
  const lastStartedRequestKeyRef = useRef('');

  const title = getReportTitle(activeTab);
  const isTransactionReport = activeTab === 'transaction-report';
  const isRentalReconSummary = activeTab === 'rental-recon-summary';
  const isUtilitiesVsRentReport = activeTab === 'utilities-vs-rent';
  const isTotalRatePerSqmReport = activeTab === 'total-rate-per-sqm';
  const isGrowthReport = growthReportKeys.has(activeTab);
  const isBatchReport = batchReportKeys.has(activeTab);
  const isEscalationReport = activeTab === 'escalations-rent' || activeTab === 'escalations-other';
  const isItemReport =
    activeTab === 'invoiced-vs-paid-rent' ||
    activeTab === 'invoiced-vs-paid-utilities' ||
    activeTab === 'contractual-charges' ||
    activeTab === 'variable-charges' ||
    activeTab === 'escalations-rent' ||
    activeTab === 'escalations-other';

  const reportFilters = useMemo(() => {
    if (isTransactionReport) {
      return transactionFilters;
    }
    if (isBatchReport) {
      return batchFilters;
    }
    if (isUtilitiesVsRentReport || isTotalRatePerSqmReport || isItemReport) {
      return utilityFilters;
    }
    if (isGrowthReport) {
      return locationFilters;
    }
    return undefined;
  }, [
    batchFilters,
    isBatchReport,
    isGrowthReport,
    isItemReport,
    isTotalRatePerSqmReport,
    isTransactionReport,
    isUtilitiesVsRentReport,
    locationFilters,
    transactionFilters,
    utilityFilters,
  ]);

  const requestFilters = useMemo(() => {
    const filters: Record<string, string> = {};
    Object.entries(reportFilters || {}).forEach(([key, value]) => {
      const textValue = String(value || '').trim();
      if (textValue) {
        filters[key] = textValue;
      }
    });
    return filters;
  }, [reportFilters]);

  const requestKey = useMemo(
    () => JSON.stringify({ activeTab, filters: requestFilters || {} }),
    [activeTab, requestFilters]
  );

  useEffect(() => {
    if (fetchReadyTab !== activeTab) {
      return;
    }
    if (lastStartedRequestKeyRef.current === requestKey) {
      return;
    }
    lastStartedRequestKeyRef.current = requestKey;

    let cancelled = false;

    const loadReport = async () => {
      setIsLoading(true);
      setError(null);
      setColumnFilters({});
      try {
        // Batch reports: lightweight metadata fetch first to discover default_filters.
        const batchFiltersEmpty =
          isBatchReport &&
          batchFilterDefinitions.every((f) => !requestFilters[f.key]);
        if (batchFiltersEmpty) {
          const meta = await fetchLMSReconReport(activeTab, 1, {});
          if (!cancelled && meta.summary?.default_filters) {
            const df = meta.summary.default_filters;
            const autoFilters: BatchFilters = {
              year: String(df.year ?? ''),
              month: String(df.month ?? ''),
              batch_no: String(df.batch_no ?? ''),
            };
            setBatchFilters(autoFilters);
          }
          return;
        }

        // Transaction report: lightweight metadata fetch to get branch list + auto-select first branch.
        const transactionNoBranch = isTransactionReport && !requestFilters['branch_code'];
        if (transactionNoBranch) {
          const meta = await fetchLMSReconReport(activeTab, 1, {});
          if (!cancelled && meta.summary?.default_filters) {
            const df = meta.summary.default_filters;
            setTransactionFilters({
              branch_code: String(df.branch_code ?? ''),
              year: String(df.year ?? new Date().getFullYear()),
              month: String(df.month ?? new Date().getMonth() + 1),
            });
          } else if (!cancelled && meta.summary?.filter_options?.branch_code?.length) {
            // Fallback: no default_filters but we have branch list — pick the first one
            const branches = meta.summary.filter_options.branch_code as string[];
            setTransactionFilters((prev) => ({
              ...prev,
              branch_code: branches[0],
            }));
          }
          return;
        }

        const report = await fetchLMSReconReport(
          activeTab,
          5000,
          requestFilters,
        );
        if (!cancelled) {
          setRows(report.rows);
          setColumns(report.columns);
          setSummary(report.summary);
        }
      } catch (loadError) {
        if (!cancelled) {
          setRows([]);
          setColumns([]);
          setSummary(undefined);
          setError(loadError instanceof Error ? loadError.message : 'Failed to load LMS Recon data');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      cancelled = true;
    };
  }, [activeTab, fetchReadyTab, isBatchReport, isTransactionReport, requestFilters, requestKey]);

  const progressDetails = useMemo(() => progressModeDetails(progressMode), [progressMode]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, batchFilters, columnFilters, locationFilters, pageSize, progressMode, tableSort, transactionFilters, utilityFilters]);

  useEffect(() => {
    setColumnFilters({});
    setTableSort(null);
    setRows([]);
    setColumns([]);
    setSummary(undefined);
    lastStartedRequestKeyRef.current = '';
    setBatchFilters(isBatchReport ? createDefaultBatchFilters(activeTab) : createEmptyBatchFilters());
    if (!isTransactionReport) {
      setTransactionFilters(createEmptyTransactionFilters());
    }
    if (!isUtilitiesVsRentReport && !isTotalRatePerSqmReport && !isItemReport) {
      setUtilityFilters(createEmptyUtilityFilters());
    }
    if (!isGrowthReport) {
      setLocationFilters(createEmptyLocationFilters());
    }
    setFetchReadyTab(activeTab);
  }, [activeTab, isBatchReport, isGrowthReport, isItemReport, isTotalRatePerSqmReport, isTransactionReport, isUtilitiesVsRentReport]);

  const visibleColumns = useMemo(() => {
    if (isItemReport) {
      const itemColumnOrder =
        activeTab === 'escalations-rent' || activeTab === 'escalations-other'
          ? escalationRentColumnOrder
          : activeTab === 'contractual-charges' || activeTab === 'variable-charges'
          ? contractualChargesColumnOrder
          : invoicedVsPaidColumnOrder;
      const visibleItemColumns = columns.filter((column) => !column.hidden);
      return [...visibleItemColumns].sort((first, second) => {
        const firstIndex = itemColumnOrder.indexOf(first.label.trim().toLowerCase());
        const secondIndex = itemColumnOrder.indexOf(second.label.trim().toLowerCase());
        const firstRank = firstIndex === -1 ? itemColumnOrder.length : firstIndex;
        const secondRank = secondIndex === -1 ? itemColumnOrder.length : secondIndex;
        return firstRank - secondRank;
      });
    }

    if (activeTab === 'growth-rent' || activeTab === 'growth-utilities') {
      const growthColumnOrder = activeTab === 'growth-utilities' ? growthUtilitiesColumnOrder : growthRentColumnOrder;
      const visibleGrowthColumns = columns.filter((column) => !column.hidden);
      return [...visibleGrowthColumns].sort((first, second) => {
        const firstIndex = growthColumnOrder.indexOf(first.label.trim().toLowerCase());
        const secondIndex = growthColumnOrder.indexOf(second.label.trim().toLowerCase());
        const firstRank = firstIndex === -1 ? growthColumnOrder.length : firstIndex;
        const secondRank = secondIndex === -1 ? growthColumnOrder.length : secondIndex;
        return firstRank - secondRank;
      });
    }

    if (activeTab !== 'progress-report') {
      return columns.filter((column) => !column.hidden);
    }
    return columns
      .filter((column) =>
        ['branch_code', 'branch_name', progressDetails.statusKey, 'captured_by'].includes(column.key)
      )
      .map((column) => (column.key === progressDetails.statusKey ? { ...column, label: 'Status' } : column));
  }, [activeTab, columns, isItemReport, progressDetails.statusKey]);

  const modeRows = useMemo(() => {
    if (activeTab !== 'progress-report') {
      return rows;
    }
    return rows.filter((row) => normalise(row.current_previous) === progressDetails.period);
  }, [activeTab, progressDetails.period, rows]);

  const hasBatchFilters = useMemo(
    () =>
      batchReportKeys.has(activeTab) &&
      batchFilterDefinitions.every((filter) => columns.some((column) => column.key === filter.key)),
    [activeTab, columns]
  );

  const batchFilterOptions = useMemo(
    () =>
      batchFilterDefinitions.reduce<Record<BatchFilterKey, string[]>>(
        (options, filter) => ({
          ...options,
          [filter.key]: filterOptionsFromSummary(summary, filter.key, modeRows),
        }),
        createEmptyBatchFilterOptions()
      ),
    [modeRows, summary]
  );

  const transactionFilterOptions = useMemo(() => {
    const sourceOptions = summary?.filter_options || {};
    return transactionFilterDefinitions.reduce<Record<TransactionFilterKey, string[]>>(
      (options, filter) => ({
        ...options,
        [filter.key]: ((sourceOptions[filter.key] || []) as Array<string | number>)
          .map((option: string | number) => String(option))
          .sort(sortOptionValues),
      }),
      createEmptyTransactionFilterOptions()
    );
  }, [summary]);

  const utilityFilterOptions = useMemo(() => {
    const sourceOptions = summary?.filter_options || {};
    return utilityFilterDefinitions.reduce<Record<UtilityFilterKey, string[]>>(
      (options, filter) => ({
        ...options,
        [filter.key]: ((sourceOptions[filter.key] || []) as Array<string | number>)
          .map((option: string | number) => String(option))
          .filter(Boolean),
      }),
      createEmptyUtilityFilterOptions()
    );
  }, [summary]);

  const locationFilterOptions = useMemo(
    () =>
      locationFilterDefinitions.reduce<Record<LocationFilterKey, string[]>>(
        (options, filter) => ({
          ...options,
          [filter.key]: filterOptionsFromSummary(summary, filter.key, modeRows),
        }),
        createEmptyLocationFilterOptions()
      ),
    [modeRows, summary]
  );

  const batchFilteredRows = useMemo(() => {
    if (!hasBatchFilters) {
      return modeRows;
    }

    return modeRows.filter((row) =>
      batchFilterDefinitions.every((filter) => {
        const selectedValue = batchFilters[filter.key];
        return !selectedValue || valueText(row[filter.key]) === selectedValue;
      })
    );
  }, [batchFilters, hasBatchFilters, modeRows]);

  const activeBatchFilters = useMemo(() => {
    const filters: Partial<BatchFilters> = {};
    batchFilterDefinitions.forEach((filter) => {
      if (batchFilters[filter.key]) {
        filters[filter.key] = batchFilters[filter.key];
      }
    });
    return Object.keys(filters).length ? filters : undefined;
  }, [batchFilters]);

  const activeTransactionFilters = useMemo(() => {
    const filters: Partial<TransactionFilters> = {};
    transactionFilterDefinitions.forEach((filter) => {
      if (transactionFilters[filter.key]) {
        filters[filter.key] = transactionFilters[filter.key];
      }
    });
    return Object.keys(filters).length ? filters : undefined;
  }, [transactionFilters]);

  const activeUtilityFilters = useMemo(() => {
    const filters: Partial<UtilityFilters> = {};
    utilityFilterDefinitions.forEach((filter) => {
      if (utilityFilters[filter.key]) {
        filters[filter.key] = utilityFilters[filter.key];
      }
    });
    return Object.keys(filters).length ? filters : undefined;
  }, [utilityFilters]);

  const activeLocationFilters = useMemo(() => {
    const filters: Partial<LocationFilters> = {};
    locationFilterDefinitions.forEach((filter) => {
      if (locationFilters[filter.key]) {
        filters[filter.key] = locationFilters[filter.key];
      }
    });
    return Object.keys(filters).length ? filters : undefined;
  }, [locationFilters]);

  const filteredRows = useMemo(() => {
    const activeFilters = Object.entries(columnFilters)
      .map(([key, filter]) => [key, filter.trim().toLowerCase()] as const)
      .filter(([, filter]) => filter);

    if (activeFilters.length === 0) {
      return batchFilteredRows;
    }

    return batchFilteredRows.filter((row) =>
      activeFilters.every(([key, filter]) => valueText(row[key]).toLowerCase().includes(filter))
    );
  }, [batchFilteredRows, columnFilters]);

  const sortedRows = useMemo(() => sortRowsByColumn(filteredRows, tableSort), [filteredRows, tableSort]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(sortedRows.length / pageSize)),
    [pageSize, sortedRows.length]
  );

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const pageNumbers = useMemo(() => {
    const visiblePages = 5;
    const firstPage = Math.max(1, Math.min(currentPage - 2, totalPages - visiblePages + 1));
    const lastPage = Math.min(totalPages, firstPage + visiblePages - 1);

    return Array.from({ length: lastPage - firstPage + 1 }, (_, index) => firstPage + index);
  }, [currentPage, totalPages]);

  const startIndex = useMemo(() => (currentPage - 1) * pageSize, [currentPage, pageSize]);
  const endIndex = useMemo(
    () => Math.min(startIndex + pageSize, sortedRows.length),
    [pageSize, sortedRows.length, startIndex]
  );
  const displayedRows = useMemo(
    () => sortedRows.slice(startIndex, endIndex),
    [endIndex, sortedRows, startIndex]
  );
  const columnByKey = useMemo(
    () => new Map(columns.map((column) => [column.key, column])),
    [columns]
  );
  const rentalSummaryDifferenceColumns = useMemo(
    () =>
      rentalSummaryDifferenceColumnKeys
        .map((key) => columnByKey.get(key))
        .filter((column): column is LMSReconColumn => Boolean(column)),
    [columnByKey]
  );
  const rentalSummaryDifferenceRows = useMemo(() => {
    if (!isRentalReconSummary) {
      return [];
    }

    return filteredRows
      .filter((row) => {
        const capturedBy = valueText(row.invoice_captured_by).trim();
        const landlordBalance = asNumber(row.landlord_statement_balance);
        return Boolean(capturedBy) && landlordBalance !== null;
      })
      .sort((first, second) => {
        const firstDifference = asNumber(first.statement_difference) || 0;
        const secondDifference = asNumber(second.statement_difference) || 0;
        return secondDifference - firstDifference;
      });
  }, [filteredRows, isRentalReconSummary]);
  const displayedRentalSummaryDifferenceRows = useMemo(
    () => sortRowsByColumn(rentalSummaryDifferenceRows, tableSort).slice(0, pageSize),
    [pageSize, rentalSummaryDifferenceRows, tableSort]
  );

  const rentalSummaryDifferenceTotals = useMemo(
    () =>
      rentalSummaryDifferenceRows.reduce<{
        landlord_statement_balance: number;
        statement_balance: number;
        statement_difference: number;
      }>(
        (acc, row) => ({
          landlord_statement_balance: acc.landlord_statement_balance + (asNumber(row.landlord_statement_balance) ?? 0),
          statement_balance: acc.statement_balance + (asNumber(row.statement_balance) ?? 0),
          statement_difference: acc.statement_difference + (asNumber(row.statement_difference) ?? 0),
        }),
        { landlord_statement_balance: 0, statement_balance: 0, statement_difference: 0 },
      ),
    [rentalSummaryDifferenceRows],
  );

  const invoicedVsPaidTotals = useMemo(() => {
    if (!isItemReport) return {} as Record<string, number>;
    return visibleColumns.reduce<Record<string, number>>((acc, column) => {
      if (!column.numeric) return acc;
      acc[column.key] = filteredRows.reduce((sum, row) => sum + (asNumber(row[column.key]) ?? 0), 0);
      return acc;
    }, {});
  }, [visibleColumns, filteredRows, isItemReport]);

  const rentalSummaryAllTotals = useMemo(() => {
    if (!isRentalReconSummary) return {} as Record<string, number>;
    return visibleColumns.reduce<Record<string, number>>((acc, column) => {
      if (!column.currency) return acc;
      acc[column.key] = filteredRows.reduce((sum, row) => sum + (asNumber(row[column.key]) ?? 0), 0);
      return acc;
    }, {});
  }, [visibleColumns, filteredRows, isRentalReconSummary]);

  const rentalSummaryMetricWidgets = useMemo(() => {
    if (!isRentalReconSummary) return [];
    const t = rentalSummaryAllTotals;
    return [
      metricWidget('rental-summary-back-charges', 'Total Back Charges', 'Total Back Charges', formatCurrencyValue(t['total_back_charges']) || 'N/A'),
      metricWidget('rental-summary-invoiced', 'Total Invoiced', 'Total Invoiced', formatCurrencyValue(t['total_invoiced']) || 'N/A'),
      metricWidget('rental-summary-paid', 'Total Paid', 'Total Paid', formatCurrencyValue(t['total_paid']) || 'N/A'),
      metricWidget('rental-summary-paid-prior-arrears', 'Total Paid Prior Arrears', 'Total Paid Prior Arrears', formatCurrencyValue(t['total_paid_prior_arrears']) || 'N/A'),
      metricWidget('rental-summary-credit-notes', 'Total Credit Notes', 'Total Credit Notes', formatCurrencyValue(t['total_credit_notes']) || 'N/A'),
      metricWidget('rental-summary-mpob', 'Total MPOB', 'Total MPOB', formatCurrencyValue(t['total_mpob']) || 'N/A'),
    ];
  }, [isRentalReconSummary, rentalSummaryAllTotals]);

  const batchPaidAmountTotal = useMemo(
    () =>
      filteredRows.reduce((total, row) => {
        const amount = asNumber(row.paid_amount);
        return amount === null ? total : total + amount;
      }, 0),
    [filteredRows]
  );
  const transactionMatrix = useMemo(() => buildTransactionMatrix(rows), [rows]);

  const utilityTableTotals = useMemo(() => {
    const totals = {
      invoiced_rent: 0,
      invoiced_utilities: 0,
      paid_rent: 0,
      paid_utilities: 0,
      invoiced_utility_rent_percent: 0,
      paid_utility_rent_percent: 0,
    };
    const counts = {
      invoiced_utility_rent_percent: 0,
      paid_utility_rent_percent: 0,
    };

    filteredRows.forEach((row) => {
      utilityCurrencyColumnKeys.forEach((key) => {
        totals[key as keyof typeof totals] += asNumber(row[key]) || 0;
      });

      utilityPercentColumnKeys.forEach((key) => {
        const value = asNumber(row[key]);
        if (value !== null) {
          totals[key as keyof typeof totals] += value;
          counts[key as keyof typeof counts] += 1;
        }
      });
    });

    utilityPercentColumnKeys.forEach((key) => {
      const count = counts[key as keyof typeof counts];
      totals[key as keyof typeof totals] = count ? totals[key as keyof typeof totals] / count : 0;
    });

    return totals;
  }, [filteredRows]);

  const ratePerSqmTableTotals = useMemo(() => {
    const totals: Record<string, number> = {
      lease_sqm: 0,
    };
    const counts: Record<string, number> = {};

    ratePerSqmAverageColumnKeys.forEach((key) => {
      totals[key] = 0;
      counts[key] = 0;
    });

    filteredRows.forEach((row) => {
      totals.lease_sqm += asNumber(row.lease_sqm) || 0;
      ratePerSqmAverageColumnKeys.forEach((key) => {
        const value = asNumber(row[key]);
        if (value !== null) {
          totals[key] += value;
          counts[key] += 1;
        }
      });
    });

    ratePerSqmAverageColumnKeys.forEach((key) => {
      totals[key] = counts[key] ? totals[key] / counts[key] : 0;
    });

    return totals;
  }, [filteredRows]);

  const growthTableTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    const counts: Record<string, number> = {};

    visibleColumns.forEach((column) => {
      if (column.numeric) {
        totals[column.key] = 0;
        counts[column.key] = 0;
      }
    });

    filteredRows.forEach((row) => {
      visibleColumns.forEach((column) => {
        if (!column.numeric || growthPercentColumnKeys.has(column.key)) {
          return;
        }
        const value = asNumber(row[column.key]);
        if (value === null) {
          return;
        }
        totals[column.key] += value;
        counts[column.key] += 1;
      });
    });

    // Growth % totals use backend sub-category level averages (matches BI's AVERAGE() DAX).
    // The backend computes these from raw (branch, sub-category) rows before branch aggregation,
    // respecting the active province/district filter.
    const backendAverages = summary?.numeric_averages || {};
    growthPercentColumnKeys.forEach((key) => {
      if (backendAverages[key] !== undefined) {
        totals[key] = backendAverages[key];
      } else if (counts[key]) {
        totals[key] = totals[key] / counts[key];
      }
    });

    return totals;
  }, [filteredRows, visibleColumns, summary]);

  const handleBatchFilterChange = (filterKey: BatchFilterKey, value: string) => {
    setBatchFilters((currentFilters) => ({
      ...currentFilters,
      [filterKey]: value,
    }));
  };

  const handleTransactionFilterChange = (filterKey: TransactionFilterKey, value: string) => {
    setTransactionFilters((currentFilters) => ({
      ...currentFilters,
      [filterKey]: value,
    }));
  };

  const handleUtilityFilterChange = (filterKey: UtilityFilterKey, value: string) => {
    setUtilityFilters((currentFilters) => ({
      ...currentFilters,
      [filterKey]: value,
    }));
  };

  const handleLocationFilterChange = (filterKey: LocationFilterKey, value: string) => {
    setLocationFilters((currentFilters) => ({
      ...currentFilters,
      [filterKey]: value,
    }));
  };

  const handleColumnFilterChange = (columnKey: string, value: string) => {
    setColumnFilters((currentFilters) => ({
      ...currentFilters,
      [columnKey]: value,
    }));
  };

  const handleTableSort = (columnKey: string) => {
    setTableSort((currentSort) => {
      if (currentSort?.key !== columnKey) {
        return { key: columnKey, direction: 'asc' };
      }

      return {
        key: columnKey,
        direction: currentSort.direction === 'asc' ? 'desc' : 'asc',
      };
    });
    setCurrentPage(1);
  };

  const metrics = useMemo(() => {
    if (activeTab === 'progress-report') {
      return progressStatuses.map((status) => ({
        label: status,
        value: formatMetricValue(
          modeRows.filter((row) => valueText(row[progressDetails.statusKey]) === status).length,
        ),
      }));
    }
    return buildMetrics(rows, columns, summary);
  }, [activeTab, columns, modeRows, progressDetails.statusKey, rows, summary]);

  const progressModeLabel = useMemo(
    () => progressModes.find(([mode]) => mode === progressMode)?.[1] || title,
    [progressMode, title]
  );

  const progressMetricWidgets = useMemo(() => {
    if (activeTab !== 'progress-report') {
      return [];
    }

    return metrics.map((metric) =>
      metricWidget(
        `lms-recon-progress-${progressMode}-${metric.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
        metric.label,
        `Show ${metric.label} rows for ${progressModeLabel} in the LMS Recon progress report`,
        metric.value,
      )
    );
  }, [activeTab, metrics, progressMode, progressModeLabel]);

  const progressGaugeWidget = useMemo(() => {
    if (activeTab !== 'progress-report') {
      return null;
    }

    const approvedCount = modeRows.filter(
      (row) => valueText(row[progressDetails.statusKey]) === 'Approved',
    ).length;

    return makeReconWidget(
      `lms-recon-progress-${progressMode}-gauge`,
      'Progress',
      `Show approved progress for ${progressModeLabel} in the LMS Recon progress report`,
      'gauge',
      [
        {
          Approved: approvedCount,
          Total: modeRows.length,
        },
      ],
    );
  }, [activeTab, modeRows, progressDetails.statusKey, progressMode, progressModeLabel]);

  const progressChartWidgets = useMemo(() => {
    if (activeTab !== 'progress-report') {
      return [];
    }

    return [
      makeReconWidget(
        `lms-recon-progress-${progressMode}-captured-by`,
        'Invoices Captured by Employee',
        `Show invoices captured by employee for ${progressModeLabel} in the LMS Recon progress report`,
        'bar_chart',
        capturedByChartRows(modeRows),
      ),
    ];
  }, [activeTab, modeRows, progressMode, progressModeLabel]);

  const utilityMetricWidgets = useMemo(() => {
    if (!isUtilitiesVsRentReport) {
      return [];
    }

    const averages = summary?.numeric_averages || {};
    return [
      metricWidget(
        'lms-recon-utilities-average-invoiced-ratio',
        'Ave. Invoiced (Utility/Rent) %',
        'Show average invoiced utility to rent percentage for Utilities vs. Rent',
        formatPercentValue(averages.invoiced_utility_rent_percent) || 'N/A',
      ),
      metricWidget(
        'lms-recon-utilities-average-paid-ratio',
        'Ave. Paid (Utility/Rent) %',
        'Show average paid utility to rent percentage for Utilities vs. Rent',
        formatPercentValue(averages.paid_utility_rent_percent) || 'N/A',
      ),
    ];
  }, [isUtilitiesVsRentReport, summary]);

  const utilityChartWidgets = useMemo(() => {
    if (!isUtilitiesVsRentReport) {
      return [];
    }

    return [
      makeReconWidget(
        'lms-recon-utilities-invoiced-by-province',
        'Average of Invoiced (Utility/Rent) % by Province',
        'Show average invoiced utility to rent percentage by province',
        'pie_chart_full',
        averageByDimension(
          modeRows,
          'province',
          'invoiced_utility_rent_percent',
          'Province',
          'Average of Invoiced (Utility/Rent) %',
        ),
      ),
      makeReconWidget(
        'lms-recon-utilities-invoiced-by-district',
        'Average of Invoiced (Utility/Rent) % by District',
        'Show average invoiced utility to rent percentage by district',
        'pie_chart_full',
        averageByDimension(
          modeRows,
          'district',
          'invoiced_utility_rent_percent',
          'District',
          'Average of Invoiced (Utility/Rent) %',
        ),
      ),
      makeReconWidget(
        'lms-recon-utilities-paid-by-province',
        'Average of Paid (Utility/Rent) % by Province',
        'Show average paid utility to rent percentage by province',
        'pie_chart_full',
        averageByDimension(
          modeRows,
          'province',
          'paid_utility_rent_percent',
          'Province',
          'Average of Paid (Utility/Rent) %',
        ),
      ),
      makeReconWidget(
        'lms-recon-utilities-paid-by-district',
        'Average of Paid (Utility/Rent) % by District',
        'Show average paid utility to rent percentage by district',
        'pie_chart_full',
        averageByDimension(
          modeRows,
          'district',
          'paid_utility_rent_percent',
          'District',
          'Average of Paid (Utility/Rent) %',
        ),
      ),
      makeReconWidget(
        'lms-recon-utilities-invoiced-scatter',
        'Sum of Invoiced (Rent), Sum of Invoiced (Utilities) and Average of Invoiced (Utility/Rent) % by Branch Code',
        'Show invoiced rent and utilities by branch code',
        'scatter_chart',
        buildUtilityScatterRows(modeRows),
      ),
    ];
  }, [isUtilitiesVsRentReport, modeRows]);

  const ratePerSqmMetricWidgets = useMemo(() => {
    if (!isTotalRatePerSqmReport) {
      return [];
    }

    const averages = summary?.numeric_averages || {};
    return [
      metricWidget(
        'lms-recon-rate-sqm-average-rent',
        'Ave. Invoiced/SQM (Rent)',
        'Show average invoiced rent per square metre for Total Rate per SQM',
        formatCurrencyValue(averages.invoiced_sqm_rent) || 'N/A',
      ),
      metricWidget(
        'lms-recon-rate-sqm-average-utilities',
        'Ave. Invoiced/SQM (Utilities)',
        'Show average invoiced utilities per square metre for Total Rate per SQM',
        formatCurrencyValue(averages.invoiced_sqm_utilities) || 'N/A',
      ),
    ];
  }, [isTotalRatePerSqmReport, summary]);

  const ratePerSqmChartWidgets = useMemo(() => {
    if (!isTotalRatePerSqmReport) {
      return [];
    }

    return [
      makeReconWidget(
        'lms-recon-rate-sqm-invoiced-rent-province',
        'Invoiced/SQM (Rent) by Province',
        'Show average invoiced rent per square metre by province',
        'pie_chart_full',
        averageByDimension(modeRows, 'province', 'invoiced_sqm_rent', 'Province', 'Invoiced/SQM (Rent)'),
      ),
      makeReconWidget(
        'lms-recon-rate-sqm-invoiced-rent-district',
        'Invoiced/SQM (Rent) by District',
        'Show average invoiced rent per square metre by district',
        'pie_chart_full',
        averageByDimension(modeRows, 'district', 'invoiced_sqm_rent', 'District', 'Invoiced/SQM (Rent)'),
      ),
      makeReconWidget(
        'lms-recon-rate-sqm-invoiced-utilities-province',
        'Invoiced/SQM (Utilities) by Province',
        'Show average invoiced utilities per square metre by province',
        'pie_chart_full',
        averageByDimension(modeRows, 'province', 'invoiced_sqm_utilities', 'Province', 'Invoiced/SQM (Utilities)'),
      ),
      makeReconWidget(
        'lms-recon-rate-sqm-invoiced-utilities-district',
        'Invoiced/SQM (Utilities) by District',
        'Show average invoiced utilities per square metre by district',
        'pie_chart_full',
        averageByDimension(modeRows, 'district', 'invoiced_sqm_utilities', 'District', 'Invoiced/SQM (Utilities)'),
      ),
      makeReconWidget(
        'lms-recon-rate-sqm-paid-rent-province',
        'Paid/SQM (Rent) by Province',
        'Show average paid rent per square metre by province',
        'pie_chart_full',
        averageByDimension(modeRows, 'province', 'paid_sqm_rent', 'Province', 'Paid/SQM (Rent)'),
      ),
      makeReconWidget(
        'lms-recon-rate-sqm-paid-rent-district',
        'Paid/SQM (Rent) by District',
        'Show average paid rent per square metre by district',
        'pie_chart_full',
        averageByDimension(modeRows, 'district', 'paid_sqm_rent', 'District', 'Paid/SQM (Rent)'),
      ),
      makeReconWidget(
        'lms-recon-rate-sqm-paid-utilities-province',
        'Paid/SQM (Utilities) by Province',
        'Show average paid utilities per square metre by province',
        'pie_chart_full',
        averageByDimension(modeRows, 'province', 'paid_sqm_utilities', 'Province', 'Paid/SQM (Utilities)'),
      ),
      makeReconWidget(
        'lms-recon-rate-sqm-paid-utilities-district',
        'Paid/SQM (Utilities) by District',
        'Show average paid utilities per square metre by district',
        'pie_chart_full',
        averageByDimension(modeRows, 'district', 'paid_sqm_utilities', 'District', 'Paid/SQM (Utilities)'),
      ),
      makeReconWidget(
        'lms-recon-rate-sqm-lease-scatter',
        'Lease SQM and Invoiced/SQM (Rent) by Branch Code',
        'Show lease square metres and invoiced rent per square metre by branch code',
        'scatter_chart',
        buildRatePerSqmScatterRows(modeRows),
      ),
    ];
  }, [isTotalRatePerSqmReport, modeRows]);

  const growthMetricWidgets = useMemo(() => {
    if (!isGrowthReport) {
      return [];
    }

    const averages = summary?.numeric_averages || {};
    const reportName = title;
    return [
      metricWidget(
        `lms-recon-${activeTab}-average-growth-1-month`,
        'Ave. Growth (1 Month)',
        `Show average one month growth for ${reportName}`,
        formatFixedNumber(averages.last_1_month_invoiced_growth) || 'N/A',
      ),
      metricWidget(
        `lms-recon-${activeTab}-average-growth-3-month`,
        'Ave. Growth (3 Month)',
        `Show average three month growth for ${reportName}`,
        formatFixedNumber(averages.last_3_month_invoiced_growth) || 'N/A',
      ),
      metricWidget(
        `lms-recon-${activeTab}-average-growth-6-month`,
        'Ave. Growth (6 Month)',
        `Show average six month growth for ${reportName}`,
        formatFixedNumber(averages.last_6_month_invoiced_growth) || 'N/A',
      ),
      metricWidget(
        `lms-recon-${activeTab}-average-growth-12-month`,
        'Ave. Growth (12 Month)',
        `Show average twelve month growth for ${reportName}`,
        formatFixedNumber(averages.last_12_month_invoiced_growth) || 'N/A',
      ),
    ];
  }, [activeTab, isGrowthReport, summary, title]);

  const growthChartWidgets = useMemo(() => {
    if (!isGrowthReport) {
      return [];
    }

    return [
      makeReconWidget(
        `lms-recon-${activeTab}-invoiced-by-province`,
        'Sum of Invoiced by Province',
        `Show sum of invoiced by province for ${title}`,
        'bar_chart',
        sumByDimension(modeRows, 'province', 'invoiced', 'Province', 'Sum of Invoiced'),
      ),
      makeReconWidget(
        `lms-recon-${activeTab}-invoiced-comparison`,
        'Invoiced compared to Previous Invoiced',
        `Show current invoiced compared with prior invoiced periods for ${title}`,
        'line_chart',
        buildGrowthComparisonRows(modeRows),
      ),
      makeReconWidget(
        `lms-recon-${activeTab}-invoiced-by-district`,
        'Sum of Invoiced by District',
        `Show sum of invoiced by district for ${title}`,
        'bar_chart',
        sumByDimension(modeRows, 'district', 'invoiced', 'District', 'Sum of Invoiced'),
      ),
      makeReconWidget(
        `lms-recon-${activeTab}-growth-location`,
        'Growth by Location (1 Month)',
        `Show one month growth by branch location for ${title}`,
        'map',
        buildGrowthLocationRows(modeRows),
      ),
    ];
  }, [activeTab, isGrowthReport, modeRows, title]);

  const visualContexts = useMemo(() => {
    let widgets: Widget[] = [];

    if (activeTab === 'progress-report') {
      widgets = [
        ...progressMetricWidgets,
        ...(progressGaugeWidget ? [progressGaugeWidget] : []),
        ...progressChartWidgets,
      ];
    } else if (isUtilitiesVsRentReport) {
      widgets = [...utilityMetricWidgets, ...utilityChartWidgets];
    } else if (isTotalRatePerSqmReport) {
      widgets = [...ratePerSqmMetricWidgets, ...ratePerSqmChartWidgets];
    } else if (isGrowthReport) {
      widgets = [...growthMetricWidgets, ...growthChartWidgets];
    }

    return widgets.map(buildWidgetVisualContext);
  }, [
    activeTab,
    growthChartWidgets,
    growthMetricWidgets,
    isGrowthReport,
    isTotalRatePerSqmReport,
    isUtilitiesVsRentReport,
    progressChartWidgets,
    progressGaugeWidget,
    progressMetricWidgets,
    ratePerSqmChartWidgets,
    ratePerSqmMetricWidgets,
    utilityChartWidgets,
    utilityMetricWidgets,
  ]);

  useEffect(() => {
    onContextChange?.({
      report_family: 'lms-recon',
      active_tab: activeTab,
      report_title: title,
      report_view: activeTab === 'progress-report'
        ? progressMode
        : isTransactionReport
          ? 'matrix'
          : 'table',
      progress_period: activeTab === 'progress-report' ? progressDetails.period : undefined,
      progress_domain: activeTab === 'progress-report' ? progressDetails.domain : undefined,
      batch_filters: isBatchReport ? activeBatchFilters : undefined,
      transaction_filters: isTransactionReport ? activeTransactionFilters : undefined,
      utility_filters: isUtilitiesVsRentReport || isTotalRatePerSqmReport || isItemReport ? activeUtilityFilters : undefined,
      location_filters: isGrowthReport ? activeLocationFilters : undefined,
      visual_contexts: visualContexts,
    });
  }, [
    activeBatchFilters,
    activeLocationFilters,
    activeTransactionFilters,
    activeUtilityFilters,
    activeTab,
    isBatchReport,
    isGrowthReport,
    isTotalRatePerSqmReport,
    isTransactionReport,
    isUtilitiesVsRentReport,
    onContextChange,
    progressDetails.domain,
    progressDetails.period,
    progressMode,
    title,
    visualContexts,
  ]);

  const askMoreWithoutWidgetId = (contextQuery: string, contextData?: any) => {
    if (!onAskMore) {
      return;
    }
    const { id, ...contextWithoutId } = contextData || {};
    onAskMore(contextQuery, contextWithoutId);
  };

  const handleReportAskMore = (contextQuery: string, contextData?: any) => {
    if (!onAskMore) {
      return;
    }
    onAskMore(contextQuery, contextData);
  };

  const renderWidget = (widget: Widget, className = '') => (
    <div key={widget.id} className={className}>
      {isLoading ? (
        <VisualLoadingCard title={widget.title} />
      ) : (
        <WidgetCard
          widget={widget}
          onDelete={() => undefined}
          onAskMore={askMoreWithoutWidgetId}
          hideManagementActions
        />
      )}
    </div>
  );

  const buildTableAskMoreContext = (
    contextQuery: string,
    sourceRows: Record<string, unknown>[],
    visualizationType = 'table',
    sourceColumns: LMSReconColumn[] = visibleColumns,
    contextTitle = title,
  ) => {
    const data = sourceRows.slice(0, 500).map((row) =>
      sourceColumns.reduce<Record<string, unknown>>((record, column) => {
        record[column.label] = row[column.key];
        return record;
      }, {})
    );

    return {
      title: contextTitle,
      users_query: contextQuery,
      generated_sql_query: 'Built-in LMS Recon report dataset',
      query_response: JSON.stringify(data),
      data,
      last_data_snapshot: data,
      source_row_count: sourceRows.length,
      visualization_type: visualizationType,
    };
  };

  const buildTransactionAskMoreContext = (contextQuery: string) => {
    const data = transactionMatrix.rows.slice(0, 500).map((row) => {
      const record: Record<string, unknown> = { Category: row.label };
      transactionMatrix.columns.forEach((column) => {
        record[column.label === 'Total' ? `${column.year} Total` : `${column.year}-${column.label}`] =
          getMatrixCellValue(row, column);
      });
      return record;
    });

    return {
      title,
      users_query: contextQuery,
      generated_sql_query: 'Built-in LMS Recon report dataset',
      query_response: JSON.stringify(data),
      data,
      last_data_snapshot: data,
      source_row_count: transactionMatrix.rows.length,
      visualization_type: 'matrix',
    };
  };

  const renderCellValue = (column: LMSReconColumn, value: unknown) => {
    if (column.currency) {
      return formatCurrencyValue(value);
    }

    if (isEscalationReport && escalationPercentColumnKeys.has(column.key)) {
      const formattedValue = formatFixedNumber(value);
      const numericValue = asNumber(value);
      if (!formattedValue || numericValue === null) {
        return formattedValue;
      }

      if (numericValue > 100) {
        return <span className="text-red-500 dark:text-red-300">{formattedValue}</span>;
      }
      if (numericValue >= 0) {
        return <span className="text-green-600 dark:text-green-300">{formattedValue}</span>;
      }
      // < 0: no color
      return formattedValue;
    }

    if (isUtilitiesVsRentReport && utilityPercentColumnKeys.has(column.key)) {
      return formatPercentValue(value);
    }

    if (activeTab === 'growth-rent' && growthPercentColumnKeys.has(column.key)) {
      const formattedValue = formatFixedNumber(value);
      const numericValue = asNumber(value);
      if (!formattedValue || numericValue === null) {
        return formattedValue;
      }

      return (
        <span className={numericValue > 0 ? 'text-red-500 dark:text-red-300' : 'text-green-600 dark:text-green-300'}>
          {formattedValue}
        </span>
      );
    }

    if (column.percent) {
      return formatFixedNumber(value);
    }

    const display = column.numeric ? formatMetricValue(value) : valueText(value);
    if (!column.key.includes('status') || !display) {
      return display;
    }

    const statusClass =
      display === 'In Progress'
        ? 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-800'
        : display === 'Awaiting Approval'
          ? 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-800'
          : display === 'Awaiting Exception Approval'
            ? 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-100 dark:border-yellow-800'
            : display === 'Approved'
              ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-200 dark:border-green-800'
              : 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700';

    return (
      <span className={`inline-flex max-w-full items-center rounded-md border px-2 py-1 text-xs font-semibold ${statusClass}`}>
        {display}
      </span>
    );
  };

  const handleTableExport = async (
    rows: Record<string, unknown>[],
    cols: LMSReconColumn[],
    sectionTitle: string,
    totalsRow?: Record<string, unknown> | null,
  ) => {
    if (exportingTable !== null) return;
    setExportingTable(sectionTitle);
    try {
      const exportRows = rows.map((row) =>
        cols.reduce<Record<string, unknown>>((acc, col) => {
          acc[col.label] = row[col.key];
          return acc;
        }, {}),
      );
      if (totalsRow) {
        const exportTotalsRow = cols.reduce<Record<string, unknown>>((acc, col, colIndex) => {
          acc[col.label] = colIndex === 0 ? 'Total' : (totalsRow[col.key] ?? '');
          return acc;
        }, {});
        exportRows.push(exportTotalsRow);
      }
      const currencyColumns = cols.filter((col) => col.currency).map((col) => col.label);
      await exportQueryResponse(exportRows, sectionTitle, 'table', undefined, undefined, totalsRow ? 1 : 0, currencyColumns);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExportingTable(null);
    }
  };

  const renderReportTable = (
    tableTitle: string,
    tableRows: Record<string, unknown>[],
    tableColumns: LMSReconColumn[],
    allRows: Record<string, unknown>[],
    options: {
      sectionClassName?: string;
      showPagination?: boolean;
      useBatchStyle?: boolean;
      showUtilityTotals?: boolean;
      showRatePerSqmTotals?: boolean;
      showGrowthTotals?: boolean;
      showStatementDiffTotals?: boolean;
      showInvoicedVsPaidTotals?: boolean;
      showRentalSummaryAllTotals?: boolean;
      sourceRowCount?: number;
      rowKeyPrefix?: string;
    } = {},
  ) => (
    <div className={`${options.sectionClassName || 'mt-6'} border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden`}>
      <div className="px-4 py-4 border-b border-slate-200 dark:border-slate-700 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="font-bold text-slate-800 dark:text-slate-100">{tableTitle}</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Showing {allRows.length ? ((options.showPagination ?? true) ? startIndex + 1 : 1) : 0} to{' '}
            {(options.showPagination ?? true) ? Math.min(startIndex + tableRows.length, allRows.length) : tableRows.length} of{' '}
            {allRows.length} rows
            {allRows.length !== (options.sourceRowCount ?? allRows.length)
              ? ` (filtered from ${options.sourceRowCount} rows)`
              : ''}
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center">
          {(options.showPagination ?? true) && (
            <label className="flex h-10 items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
              Show:
              <select
                value={pageSize}
                onChange={(event) => setPageSize(Number(event.target.value))}
                className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm font-medium dark:border-slate-600 dark:bg-slate-800"
              >
                {[10, 25, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          )}
          {onAskMore && (
            <button
              type="button"
              onClick={() => {
                const contextQuery = `Ask about ${tableTitle} data`;
                handleReportAskMore(
                  contextQuery,
                  buildTableAskMoreContext(contextQuery, allRows, 'table', tableColumns, tableTitle),
                );
              }}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-emerald-500 px-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-600"
              title="Ask more about this data"
            >
              <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                auto_awesome
              </span>
              Ask More
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              const exportTotals: Record<string, unknown> | null = (() => {
                if (options.useBatchStyle) return { paid_amount: batchPaidAmountTotal };
                if (options.showGrowthTotals) return growthTableTotals as Record<string, unknown>;
                if (options.showUtilityTotals) return utilityTableTotals as Record<string, unknown>;
                if (options.showRatePerSqmTotals) return ratePerSqmTableTotals as Record<string, unknown>;
                if (options.showStatementDiffTotals) return rentalSummaryDifferenceTotals as Record<string, unknown>;
                if (options.showInvoicedVsPaidTotals) return invoicedVsPaidTotals as Record<string, unknown>;
                if (options.showRentalSummaryAllTotals) return rentalSummaryAllTotals as Record<string, unknown>;
                return null;
              })();
              handleTableExport(allRows, tableColumns, tableTitle, exportTotals);
            }}
            disabled={exportingTable === tableTitle}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-blue-600 text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Export"
          >
            {exportingTable === tableTitle ? (
              <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            )}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead
            className={
              options.useBatchStyle
                ? 'bg-red-700 text-white'
                : 'bg-slate-50 text-slate-600 dark:bg-slate-800/80 dark:text-slate-300'
            }
          >
            <tr>
              {tableColumns.map((column) => (
                <th
                  key={column.key}
                  className={`px-4 py-3 text-left font-bold whitespace-nowrap ${
                    options.useBatchStyle ? 'border border-red-800' : ''
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => handleTableSort(column.key)}
                    className={`group inline-flex items-center gap-1.5 text-left font-bold ${
                      options.useBatchStyle
                        ? 'text-white'
                        : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white'
                    }`}
                    title={`Sort by ${column.label}`}
                  >
                    <span>{column.label}</span>
                    <span
                      className={`material-symbols-outlined text-[15px] leading-none transition-opacity ${
                        tableSort?.key === column.key ? 'opacity-100' : 'opacity-35 group-hover:opacity-80'
                      }`}
                      aria-hidden="true"
                      style={{ fontSize: '15px' }}
                    >
                      {tableSort?.key === column.key
                        ? tableSort.direction === 'asc'
                          ? 'arrow_upward'
                          : 'arrow_downward'
                        : 'unfold_more'}
                    </span>
                  </button>
                </th>
              ))}
            </tr>
            <tr className="bg-slate-50 text-slate-600 dark:bg-slate-800/80 dark:text-slate-300">
              {tableColumns.map((column) => (
                <th key={`${column.key}-filter`} className="px-4 pb-3">
                  <input
                    type="search"
                    value={columnFilters[column.key] || ''}
                    onChange={(event) => handleColumnFilterChange(column.key, event.target.value)}
                    className="w-full min-w-40 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-normal text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                    placeholder={`Filter ${column.label}...`}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {isLoading ? (
              <TableLoadingRows columnCount={tableColumns.length} rowCount={Math.min(pageSize, 8)} />
            ) : tableRows.length === 0 ? (
              <tr>
                <td
                  className="px-4 py-8 text-center text-slate-500"
                  colSpan={Math.max(tableColumns.length, 1)}
                >
                  No data available
                </td>
              </tr>
            ) : (
              tableRows.map((row, rowIndex) => (
                <tr
                  key={`${options.rowKeyPrefix || activeTab}-${valueText(row.branch_code)}-${rowIndex}`}
                  className="bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/70"
                >
                  {tableColumns.map((column) => (
                    <td
                      key={column.key}
                      className="px-4 py-3 whitespace-nowrap text-slate-700 dark:text-slate-200"
                    >
                      {options.useBatchStyle
                        ? formatBatchCellValue(column, row[column.key])
                        : renderCellValue(column, row[column.key])}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
          {options.useBatchStyle && !isLoading && filteredRows.length > 0 && (
            <tfoot>
              <tr className="bg-slate-500 font-bold text-white">
                {tableColumns.map((column, columnIndex) => (
                  <td
                    key={`total-${column.key}`}
                    className={`border border-slate-400 px-4 py-2 whitespace-nowrap ${
                      column.key === 'paid_amount' ? 'text-right' : 'text-left'
                    }`}
                  >
                    {columnIndex === 0
                      ? 'Total'
                      : column.key === 'paid_amount'
                        ? formatBatchAmount(batchPaidAmountTotal)
                        : ''}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
          {options.showUtilityTotals && !isLoading && filteredRows.length > 0 && (
            <tfoot>
              <tr className="bg-slate-500 font-bold text-white">
                {tableColumns.map((column, columnIndex) => {
                  let totalValue = '';
                  if (column.currency) {
                    totalValue = formatCurrencyValue(utilityTableTotals[column.key as keyof typeof utilityTableTotals]);
                  } else if (utilityPercentColumnKeys.has(column.key)) {
                    totalValue = formatPercentValue(utilityTableTotals[column.key as keyof typeof utilityTableTotals]);
                  }

                  return (
                    <td
                      key={`utility-total-${column.key}`}
                      className={`border border-slate-400 px-4 py-2 whitespace-nowrap ${
                        column.numeric ? 'text-right' : 'text-left'
                      }`}
                    >
                      {columnIndex === 0 ? 'Total' : totalValue}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
          {options.showRatePerSqmTotals && !isLoading && filteredRows.length > 0 && (
            <tfoot>
              <tr className="bg-slate-500 font-bold text-white">
                {tableColumns.map((column, columnIndex) => {
                  let totalValue = '';
                  if (column.key === 'lease_sqm') {
                    totalValue = formatFixedNumber(ratePerSqmTableTotals.lease_sqm);
                  } else if (column.currency) {
                    totalValue = formatCurrencyValue(ratePerSqmTableTotals[column.key]);
                  }

                  return (
                    <td
                      key={`rate-sqm-total-${column.key}`}
                      className={`border border-slate-400 px-4 py-2 whitespace-nowrap ${
                        column.numeric ? 'text-right' : 'text-left'
                      }`}
                    >
                      {columnIndex === 0 ? 'Total' : totalValue}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
          {options.showStatementDiffTotals && !isLoading && rentalSummaryDifferenceRows.length > 0 && (
            <tfoot>
              <tr className="bg-slate-500 font-bold text-white">
                {tableColumns.map((column, columnIndex) => (
                  <td
                    key={`stmt-diff-total-${column.key}`}
                    className={`border border-slate-400 px-4 py-2 whitespace-nowrap ${
                      column.numeric ? 'text-right' : 'text-left'
                    }`}
                  >
                    {columnIndex === 0
                      ? 'Total'
                      : statementDiffNumericColumnKeys.has(column.key)
                        ? formatCurrencyValue(rentalSummaryDifferenceTotals[column.key as keyof typeof rentalSummaryDifferenceTotals])
                        : ''}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
          {options.showInvoicedVsPaidTotals && !isLoading && filteredRows.length > 0 && (
            <tfoot>
              <tr className="bg-slate-500 font-bold text-white">
                {tableColumns.map((column, columnIndex) => (
                  <td
                    key={`inv-paid-total-${column.key}`}
                    className={`border border-slate-400 px-4 py-2 whitespace-nowrap ${
                      column.numeric ? 'text-right' : 'text-left'
                    }`}
                  >
                    {columnIndex === 0
                      ? 'Total'
                      : column.numeric && invoicedVsPaidTotals[column.key] !== undefined
                        ? formatCurrencyValue(invoicedVsPaidTotals[column.key])
                        : ''}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
          {options.showGrowthTotals && !isLoading && filteredRows.length > 0 && (
            <tfoot>
              <tr className="bg-slate-500 font-bold text-white">
                {tableColumns.map((column, columnIndex) => {
                  let totalValue = '';
                  if (column.currency) {
                    totalValue = formatCurrencyValue(growthTableTotals[column.key]);
                  } else if (growthPercentColumnKeys.has(column.key)) {
                    totalValue = formatFixedNumber(growthTableTotals[column.key]);
                  }

                  return (
                    <td
                      key={`growth-total-${column.key}`}
                      className={`border border-slate-400 px-4 py-2 whitespace-nowrap ${
                        column.numeric ? 'text-right' : 'text-left'
                      }`}
                    >
                      {columnIndex === 0 ? 'Total' : totalValue}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
          {options.showRentalSummaryAllTotals && !isLoading && filteredRows.length > 0 && (
            <tfoot>
              <tr className="bg-slate-500 font-bold text-white">
                {tableColumns.map((column, columnIndex) => (
                  <td
                    key={`rental-summary-all-total-${column.key}`}
                    className={`border border-slate-400 px-4 py-2 whitespace-nowrap ${
                      column.numeric ? 'text-right' : 'text-left'
                    }`}
                  >
                    {columnIndex === 0
                      ? 'Total'
                      : column.currency && rentalSummaryAllTotals[column.key] !== undefined
                        ? formatCurrencyValue(rentalSummaryAllTotals[column.key])
                        : ''}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {(options.showPagination ?? true) && allRows.length > pageSize && (
        <div className="flex flex-col gap-3 border-t border-slate-200 px-4 py-3 dark:border-slate-700 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={currentPage === 1}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Previous
            </button>
            {pageNumbers.map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => setCurrentPage(page)}
                className={`min-w-9 rounded-md border px-3 py-1.5 text-sm font-semibold transition-colors ${
                  currentPage === page
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : 'border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              disabled={currentPage === totalPages}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <section className="bg-white/90 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm overflow-hidden">
      <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-700 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">
            LMS Recon Analysis
          </div>
          <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">{title}</h2>
        </div>
      </div>

      {isBatchReport && (
        <div className="px-6 pt-5">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {batchFilterDefinitions.map((filter) => (
              <fieldset key={filter.key} className="min-w-0">
                <legend className="mb-2 text-sm font-bold text-slate-700 dark:text-slate-200">{filter.label}</legend>
                <div className="max-h-36 overflow-y-auto pr-2">
                  <label className="flex items-center gap-3 py-1.5 text-sm text-slate-700 dark:text-slate-200">
                    <input
                      type="radio"
                      name={`batch-${filter.key}`}
                      value=""
                      checked={batchFilters[filter.key] === ''}
                      onChange={(event) => handleBatchFilterChange(filter.key, event.target.value)}
                      className="h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    All
                  </label>
                  {(
                    batchFilters[filter.key] && !batchFilterOptions[filter.key].includes(batchFilters[filter.key])
                      ? [batchFilters[filter.key], ...batchFilterOptions[filter.key]]
                      : batchFilterOptions[filter.key]
                  ).map((option) => (
                    <label
                      key={`${filter.key}-${option}`}
                      className="flex items-center gap-3 py-1.5 text-sm text-slate-700 dark:text-slate-200"
                    >
                      <input
                        type="radio"
                        name={`batch-${filter.key}`}
                        value={option}
                        checked={batchFilters[filter.key] === option}
                        onChange={(event) => handleBatchFilterChange(filter.key, event.target.value)}
                        className="h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      {option}
                    </label>
                  ))}
                </div>
              </fieldset>
            ))}
          </div>
        </div>
      )}

      {isTransactionReport && (
        <div className="px-6 pt-5">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {transactionFilterDefinitions.map((filter) => (
              <fieldset key={filter.key} className="min-w-0">
                <legend className="mb-2 text-sm font-bold text-slate-700 dark:text-slate-200">{filter.label}</legend>
                <div className="max-h-44 overflow-y-auto pr-2">
                  {transactionFilterOptions[filter.key].map((option) => (
                    <label
                      key={`${filter.key}-${option}`}
                      className="flex items-center gap-3 py-1.5 text-sm text-slate-700 dark:text-slate-200"
                    >
                      <input
                        type="radio"
                        name={`transaction-${filter.key}`}
                        value={option}
                        checked={transactionFilters[filter.key] === option}
                        onChange={(event) => handleTransactionFilterChange(filter.key, event.target.value)}
                        className="h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      {option}
                    </label>
                  ))}
                </div>
              </fieldset>
            ))}
          </div>
        </div>
      )}

      {(isUtilitiesVsRentReport || isTotalRatePerSqmReport || isItemReport) && (
        <div className={isItemReport ? 'border-b border-slate-200 px-6 py-3 dark:border-slate-700' : 'px-6 pt-5'}>
          {isItemReport ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-sm font-bold text-slate-700 dark:text-slate-200">Date</span>
              {(
                utilityFilters.date && !utilityFilterOptions.date.includes(utilityFilters.date)
                  ? [utilityFilters.date, ...utilityFilterOptions.date]
                  : utilityFilterOptions.date
              ).map((option) => {
                const isSelected = utilityFilters.date === option;
                return (
                  <label
                    key={`utility-date-${option}`}
                    className={`inline-flex h-9 cursor-pointer items-center rounded-md border px-3 text-sm font-medium transition-colors ${
                      isSelected
                        ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                        : 'border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800'
                    }`}
                  >
                    <input
                      type="radio"
                      name="utility-date"
                      value={option}
                      checked={isSelected}
                      onChange={(event) => handleUtilityFilterChange('date', event.target.value)}
                      className="sr-only"
                    />
                    {option}
                  </label>
                );
              })}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {utilityFilterDefinitions.map((filter) => (
                <fieldset key={filter.key} className="min-w-0">
                  <legend className="mb-2 text-sm font-bold text-slate-700 dark:text-slate-200">{filter.label}</legend>
                  <div className="max-h-36 overflow-y-auto pr-2">
                    {filter.key !== 'date' && (
                      <label className="flex items-center gap-3 py-1.5 text-sm text-slate-700 dark:text-slate-200">
                        <input
                          type="radio"
                          name={`utility-${filter.key}`}
                          value=""
                          checked={utilityFilters[filter.key] === ''}
                          onChange={(event) => handleUtilityFilterChange(filter.key, event.target.value)}
                          className="h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        All
                      </label>
                    )}
                    {(
                      utilityFilters[filter.key] && !utilityFilterOptions[filter.key].includes(utilityFilters[filter.key])
                        ? [utilityFilters[filter.key], ...utilityFilterOptions[filter.key]]
                        : utilityFilterOptions[filter.key]
                    ).map((option) => (
                      <label
                        key={`${filter.key}-${option}`}
                        className="flex items-center gap-3 py-1.5 text-sm text-slate-700 dark:text-slate-200"
                      >
                        <input
                          type="radio"
                          name={`utility-${filter.key}`}
                          value={option}
                          checked={utilityFilters[filter.key] === option}
                          onChange={(event) => handleUtilityFilterChange(filter.key, event.target.value)}
                          className="h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                </fieldset>
              ))}
            </div>
          )}
        </div>
      )}

      {isGrowthReport && (
        <div className="px-6 pt-5">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {locationFilterDefinitions.map((filter) => (
              <fieldset key={filter.key} className="min-w-0">
                <legend className="mb-2 text-sm font-bold text-slate-700 dark:text-slate-200">{filter.label}</legend>
                <div className="max-h-36 overflow-y-auto pr-2">
                  {filter.key !== 'date' && (
                    <label className="flex items-center gap-3 py-1.5 text-sm text-slate-700 dark:text-slate-200">
                      <input
                        type="radio"
                        name={`location-${filter.key}`}
                        value=""
                        checked={locationFilters[filter.key] === ''}
                        onChange={(event) => handleLocationFilterChange(filter.key, event.target.value)}
                        className="h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      All
                    </label>
                  )}
                  {(
                    locationFilters[filter.key] && !locationFilterOptions[filter.key].includes(locationFilters[filter.key])
                      ? [locationFilters[filter.key], ...locationFilterOptions[filter.key]]
                      : locationFilterOptions[filter.key]
                  ).map((option) => (
                    <label
                      key={`${filter.key}-${option}`}
                      className="flex items-center gap-3 py-1.5 text-sm text-slate-700 dark:text-slate-200"
                    >
                      <input
                        type="radio"
                        name={`location-${filter.key}`}
                        value={option}
                        checked={locationFilters[filter.key] === option}
                        onChange={(event) => handleLocationFilterChange(filter.key, event.target.value)}
                        className="h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      {option}
                    </label>
                  ))}
                </div>
              </fieldset>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'progress-report' && (
        <div className="px-6 pt-5 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {progressModes.map(([mode, label]) => (
            <button
              key={mode}
              type="button"
              onClick={() => setProgressMode(mode)}
              className={`px-4 py-3 border text-sm font-semibold transition-colors ${
                progressMode === mode
                  ? 'bg-red-500 text-white border-red-500'
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600 hover:border-blue-400'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <div className="px-6 py-5">
        {error ? (
          <div className="px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
            {error}
          </div>
        ) : (
          <>
            {activeTab === 'progress-report' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  {progressMetricWidgets.map((widget) => renderWidget(widget, 'min-h-[120px]'))}
                </div>
                <div className="grid grid-cols-1 items-stretch gap-6 xl:grid-cols-2">
                  {progressGaugeWidget && renderWidget(progressGaugeWidget, 'lms-recon-progress-visual h-96')}
                  {progressChartWidgets.map((widget) => renderWidget(widget, 'lms-recon-progress-visual h-96'))}
                </div>
              </div>
            )}

            {isUtilitiesVsRentReport && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {utilityMetricWidgets.map((widget) => renderWidget(widget, 'min-h-[120px]'))}
                </div>
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                  {utilityChartWidgets.slice(0, 4).map((widget) => renderWidget(widget, 'h-96'))}
                </div>
                {utilityChartWidgets[4] && renderWidget(utilityChartWidgets[4], 'h-[420px]')}
              </div>
            )}

            {isTotalRatePerSqmReport && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {ratePerSqmMetricWidgets.map((widget) => renderWidget(widget, 'min-h-[120px]'))}
                </div>
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                  {ratePerSqmChartWidgets.slice(0, 8).map((widget) => renderWidget(widget, 'h-96'))}
                </div>
                {ratePerSqmChartWidgets[8] && renderWidget(ratePerSqmChartWidgets[8], 'h-[420px]')}
              </div>
            )}

            {isGrowthReport && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  {growthMetricWidgets.map((widget) => renderWidget(widget, 'min-h-[120px]'))}
                </div>
              </div>
            )}

            {isTransactionReport ? (
              <div className="mt-6 overflow-hidden">
                <div className="mb-2 flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Transaction Report</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {transactionFilters.branch_code || 'All branches'}
                    </p>
                  </div>
                  {onAskMore && (
                    <button
                      type="button"
                      onClick={() => {
                        const contextQuery = `Ask about ${title} matrix`;
                        handleReportAskMore(contextQuery, buildTransactionAskMoreContext(contextQuery));
                      }}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-emerald-500 px-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-600"
                      title="Ask more about this data"
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                        auto_awesome
                      </span>
                      Ask More
                    </button>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-red-700 text-white">
                        <th className="sticky left-0 z-20 min-w-64 border border-red-800 bg-red-700 px-2 py-1 text-left font-bold">
                          Year
                        </th>
                        {transactionMatrix.yearGroups.map((group) => (
                          <th
                            key={group.year}
                            className="border border-red-800 px-2 py-1 text-left font-bold"
                            colSpan={group.columns.length}
                          >
                            {group.year}
                          </th>
                        ))}
                        <th className="border border-red-800 px-2 py-1 text-left font-bold" rowSpan={2}>
                          Total
                        </th>
                      </tr>
                      <tr className="bg-red-700 text-white">
                        <th className="sticky left-0 z-20 min-w-64 border border-red-800 bg-red-700 px-2 py-1 text-left font-bold">
                          Category
                        </th>
                        {transactionMatrix.yearGroups.flatMap((group) =>
                          group.columns.map((column) => (
                            <th
                              key={column.key}
                              className="min-w-28 border border-red-800 px-2 py-1 text-left font-bold"
                            >
                              {column.label}
                            </th>
                          ))
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <TableLoadingRows
                          columnCount={Math.max(transactionMatrix.columns.length + 1, 4)}
                          rowCount={Math.min(pageSize, 8)}
                        />
                      ) : transactionMatrix.rows.length === 0 ? (
                        <tr>
                          <td
                            className="border border-slate-200 px-4 py-8 text-center text-slate-500 dark:border-slate-700"
                            colSpan={Math.max(transactionMatrix.columns.length + 1, 2)}
                          >
                            {!transactionFilters.branch_code
                              ? 'Select a branch to load transactions'
                              : 'No data available for the selected filters'}
                          </td>
                        </tr>
                      ) : (
                        transactionMatrix.rows.map((row) => (
                          <tr
                            key={row.key}
                            className={`${row.bold ? 'font-bold text-slate-950 dark:text-slate-50' : 'text-slate-700 dark:text-slate-200'} ${
                              row.key === 'total' ? 'bg-slate-50 dark:bg-slate-800/80' : 'bg-white dark:bg-slate-900'
                            }`}
                          >
                            <th
                              className={`sticky left-0 z-10 border border-slate-200 bg-inherit px-2 py-1 text-left whitespace-nowrap dark:border-slate-700 ${
                                row.level === 1 ? 'pl-8 font-normal' : ''
                              }`}
                            >
                              {row.level === 0 && row.key !== 'total' ? (
                                <span className="mr-2 text-slate-500">+</span>
                              ) : null}
                              {row.label}
                            </th>
                            {transactionMatrix.columns.map((column) => (
                              <td
                                key={`${row.key}-${column.key}`}
                                className={`border border-slate-200 px-2 py-1 text-right whitespace-nowrap dark:border-slate-700 ${
                                  column.type !== 'month' ? 'font-bold' : ''
                                }`}
                              >
                                {formatMatrixValue(getMatrixCellValue(row, column))}
                              </td>
                            ))}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : isRentalReconSummary ? (
              <div className="mt-6 space-y-8">
                {rentalSummaryMetricWidgets.length > 0 && (
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
                    {rentalSummaryMetricWidgets.map((widget) => renderWidget(widget, 'min-h-[100px]'))}
                  </div>
                )}
                {renderReportTable(
                  'Statement Differences for Captured Recons',
                  displayedRentalSummaryDifferenceRows,
                  rentalSummaryDifferenceColumns,
                  sortRowsByColumn(rentalSummaryDifferenceRows, tableSort),
                  {
                    sectionClassName: '',
                    showPagination: false,
                    sourceRowCount: rentalSummaryDifferenceRows.length,
                    rowKeyPrefix: 'rental-summary-differences',
                    showStatementDiffTotals: true,
                  },
                )}

                {renderReportTable(
                  'Recon Summary for All Recons',
                  displayedRows,
                  visibleColumns,
                  sortedRows,
                  {
                    sectionClassName: '',
                    showPagination: true,
                    sourceRowCount: modeRows.length,
                    rowKeyPrefix: 'rental-summary-all',
                    showRentalSummaryAllTotals: true,
                  },
                )}
              </div>
            ) : (
              renderReportTable(
                isBatchReport ? title : `${title} Data`,
                displayedRows,
                visibleColumns,
                sortedRows,
                {
                  sectionClassName: isItemReport ? 'mt-0' : undefined,
                  sourceRowCount: modeRows.length,
                  useBatchStyle: isBatchReport,
                  showUtilityTotals: isUtilitiesVsRentReport,
                  showRatePerSqmTotals: isTotalRatePerSqmReport,
                  showGrowthTotals: isGrowthReport,
                  showInvoicedVsPaidTotals: isItemReport && !isEscalationReport,
                },
              )
            )}

            {isGrowthReport && (
              <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
                {growthChartWidgets.map((widget) =>
                  renderWidget(
                    widget,
                    widget.visualization_type === 'map' ? 'h-[460px] xl:col-span-2' : 'h-96',
                  )
                )}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default LMSReconDashboard;
